import React, { useState, useEffect } from 'react';
import {
  MapPin,
  Target,
  TrendingUp,
  TrendingDown,
  Zap,
  Activity,
  Play,
  Pause,
  RefreshCw,
  Settings,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  DollarSign,
  Fuel,
  Users,
  Navigation
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';

interface CompetitorData {
  id: string;
  name: string;
  brand: string;
  distance: number;
  price: number;
  volume: number;
  marketShare: number;
  position: { x: number; y: number };
  color: string;
  strengths: string[];
  weaknesses: string[];
}

interface EnhancedCompetitorAnalysisProps {
  data: any;
}

export default function EnhancedCompetitorAnalysis({ data }: EnhancedCompetitorAnalysisProps) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState('');
  const [progress, setProgress] = useState(0);
  const [selectedCompetitor, setSelectedCompetitor] = useState<string | null>(null);
  const [showInsights, setShowInsights] = useState(false);

  // Enhanced competitor data with positioning
  const competitors: CompetitorData[] = [
    {
      id: 'chevron-1',
      name: 'Chevron',
      brand: 'Chevron',
      distance: 0.3,
      price: 4.32,
      volume: 135000,
      marketShare: 0.24,
      position: { x: 200, y: 120 },
      color: '#ef4444',
      strengths: ['Premium brand', 'High traffic location'],
      weaknesses: ['Higher pricing', 'Limited convenience store']
    },
    {
      id: 'arco-1',
      name: 'Arco',
      brand: 'Arco',
      distance: 0.5,
      price: 4.18,
      volume: 120000,
      marketShare: 0.21,
      position: { x: 450, y: 140 },
      color: '#3b82f6',
      strengths: ['Competitive pricing', 'Good visibility'],
      weaknesses: ['Cash only pumps', 'Older facilities']
    },
    {
      id: 'mobil-1',
      name: 'Mobil',
      brand: 'Mobil',
      distance: 0.8,
      price: 4.28,
      volume: 110000,
      marketShare: 0.19,
      position: { x: 150, y: 300 },
      color: '#10b981',
      strengths: ['24/7 operations', 'Car wash facility'],
      weaknesses: ['Limited parking', 'High maintenance costs']
    },
    {
      id: 'independent-1',
      name: 'Fast Fuel',
      brand: 'Independent',
      distance: 1.2,
      price: 4.15,
      volume: 45000,
      marketShare: 0.08,
      position: { x: 500, y: 320 },
      color: '#f59e0b',
      strengths: ['Lowest prices', 'Local loyalty'],
      weaknesses: ['Limited marketing', 'Aging equipment']
    }
  ];

  const subjectProperty = {
    name: 'Shell Station - Main St',
    position: { x: 300, y: 200 },
    price: 4.25,
    volume: 150000,
    marketShare: 0.28
  };

  const gameTheoryResults = {
    currentStrategy: 'Premium Service Focus',
    optimalPrice: 4.23,
    expectedVolume: 155000,
    expectedRevenue: 655650,
    marketShare: 0.31,
    scenarios: [
      {
        name: '5¢ Price Decrease',
        volumeChange: 12000,
        revenueChange: 25000,
        roi: 0.15,
        competitorResponse: 'Likely price match within 48 hours',
        marketShareGain: 0.03
      },
      {
        name: 'Premium Service Enhancement',
        volumeChange: 8000,
        revenueChange: 35000,
        roi: 0.22,
        competitorResponse: 'Minimal immediate response',
        marketShareGain: 0.02
      },
      {
        name: 'Loyalty Program Launch',
        volumeChange: 15000,
        revenueChange: 42000,
        roi: 0.28,
        competitorResponse: 'Delayed competitive programs',
        marketShareGain: 0.04
      }
    ]
  };

  const runAnalysis = async () => {
    setIsAnalyzing(true);
    setAnalysisComplete(false);
    setProgress(0);
    setShowInsights(false);

    const phases = [
      { name: 'Scanning competitor locations...', duration: 800, progress: 15 },
      { name: 'Analyzing pricing strategies...', duration: 1000, progress: 35 },
      { name: 'Processing volume data...', duration: 700, progress: 55 },
      { name: 'Running game theory models...', duration: 900, progress: 80 },
      { name: 'Generating strategic insights...', duration: 600, progress: 100 }
    ];

    for (const phase of phases) {
      setAnalysisPhase(phase.name);
      await new Promise(resolve => setTimeout(resolve, phase.duration));
      setProgress(phase.progress);
    }

    setAnalysisComplete(true);
    setIsAnalyzing(false);
    setTimeout(() => setShowInsights(true), 500);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Competitive Intelligence Analysis
            </h2>
            <p className="text-gray-600">
              Advanced competitor mapping with game theory optimization and strategic insights
            </p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500 mb-1">Analysis Radius</div>
            <div className="text-2xl font-bold text-purple-600">2.0 mi</div>
            <div className="text-sm text-gray-500">4 Competitors</div>
          </div>
        </div>

        {/* Analysis Controls */}
        <div className="flex items-center gap-4">
          <Button
            onClick={runAnalysis}
            disabled={isAnalyzing}
            className={`flex items-center gap-2 ${
              isAnalyzing
                ? 'bg-orange-500 hover:bg-orange-600'
                : analysisComplete
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-blue-600 hover:bg-blue-700'
            } text-white`}
          >
            {isAnalyzing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Analyzing... {progress}%
              </>
            ) : analysisComplete ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Analysis Complete
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run Analysis
              </>
            )}
          </Button>

          {isAnalyzing && (
            <div className="flex-1 max-w-md">
              <div className="text-sm text-gray-600 mb-1">{analysisPhase}</div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Analysis Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Interactive Map */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Competitive Landscape</h3>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-green-600">Live Data</span>
              </div>
            </div>

            <div className="relative">
              {/* Enhanced SVG Map */}
              <svg className="w-full h-96 rounded-lg border border-gray-200" viewBox="0 0 600 400">
                {/* Background */}
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
                  </pattern>

                  {/* Gradient for influence zones */}
                  <radialGradient id="subjectInfluence" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="rgba(139, 92, 246, 0.3)" />
                    <stop offset="100%" stopColor="rgba(139, 92, 246, 0.05)" />
                  </radialGradient>
                </defs>

                <rect width="600" height="400" fill="url(#grid)" />

                {/* Major Roads */}
                <g stroke="#9ca3af" strokeWidth="4" fill="none">
                  <line x1="0" y1="200" x2="600" y2="200" />
                  <text x="300" y="190" textAnchor="middle" fontSize="14" fill="#6b7280" fontWeight="bold">
                    Main Street
                  </text>

                  <line x1="300" y1="0" x2="300" y2="400" />
                  <text x="310" y="100" fontSize="14" fill="#6b7280" fontWeight="bold">
                    Oak Avenue
                  </text>
                </g>

                {/* Secondary Roads */}
                <g stroke="#d1d5db" strokeWidth="2" fill="none">
                  <line x1="150" y1="0" x2="150" y2="400" />
                  <line x1="450" y1="0" x2="450" y2="400" />
                  <line x1="0" y1="100" x2="600" y2="100" />
                  <line x1="0" y1="300" x2="600" y2="300" />
                </g>

                {/* Subject Property Influence Zone */}
                {(analysisComplete || showInsights) && (
                  <g className="animate-fade-in">
                    <circle
                      cx={subjectProperty.position.x}
                      cy={subjectProperty.position.y}
                      r="80"
                      fill="url(#subjectInfluence)"
                      className="animate-pulse"
                    />
                  </g>
                )}

                {/* Competitor Locations */}
                {competitors.map((competitor, index) => (
                  <g key={competitor.id} className={`cursor-pointer ${analysisComplete ? 'animate-bounce-in' : ''}`}
                     style={{ animationDelay: `${index * 200}ms` }}
                     onClick={() => setSelectedCompetitor(competitor.id)}>

                    {/* Market Influence Circle */}
                    {analysisComplete && (
                      <circle
                        cx={competitor.position.x}
                        cy={competitor.position.y}
                        r={competitor.marketShare * 120}
                        fill={competitor.color}
                        fillOpacity="0.1"
                        stroke={competitor.color}
                        strokeWidth="1"
                        strokeDasharray="3,3"
                        className="animate-fade-in"
                        style={{ animationDelay: `${index * 300 + 1000}ms` }}
                      />
                    )}

                    {/* Station Marker */}
                    <circle
                      cx={competitor.position.x}
                      cy={competitor.position.y}
                      r="12"
                      fill={competitor.color}
                      stroke="#ffffff"
                      strokeWidth="3"
                      className={`${selectedCompetitor === competitor.id ? 'animate-ping' : ''}`}
                    />
                    <circle
                      cx={competitor.position.x}
                      cy={competitor.position.y}
                      r="6"
                      fill="#ffffff"
                    />

                    {/* Label */}
                    <text
                      x={competitor.position.x}
                      y={competitor.position.y + 25}
                      textAnchor="middle"
                      fontSize="11"
                      fill={competitor.color}
                      fontWeight="bold"
                    >
                      {competitor.name}
                    </text>
                    <text
                      x={competitor.position.x}
                      y={competitor.position.y + 37}
                      textAnchor="middle"
                      fontSize="9"
                      fill="#6b7280"
                    >
                      {competitor.distance} mi • ${competitor.price.toFixed(2)}
                    </text>

                    {/* Volume indicator */}
                    {analysisComplete && (
                      <rect
                        x={competitor.position.x + 15}
                        y={competitor.position.y - 20}
                        width={Math.max(competitor.volume / 3000, 20)}
                        height="4"
                        fill={competitor.color}
                        opacity="0.7"
                        rx="2"
                        className="animate-grow-width"
                        style={{ animationDelay: `${index * 200 + 1500}ms` }}
                      />
                    )}
                  </g>
                ))}

                {/* Subject Property */}
                <g className="cursor-pointer" onClick={() => setSelectedCompetitor(null)}>
                  <circle
                    cx={subjectProperty.position.x}
                    cy={subjectProperty.position.y}
                    r="15"
                    fill="#8b5cf6"
                    stroke="#ffffff"
                    strokeWidth="4"
                  />
                  <circle
                    cx={subjectProperty.position.x}
                    cy={subjectProperty.position.y}
                    r="8"
                    fill="#ffffff"
                  />
                  <text
                    x={subjectProperty.position.x}
                    y={subjectProperty.position.y + 30}
                    textAnchor="middle"
                    fontSize="12"
                    fill="#8b5cf6"
                    fontWeight="bold"
                  >
                    Your Station (Shell)
                  </text>
                  <text
                    x={subjectProperty.position.x}
                    y={subjectProperty.position.y + 42}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#6b7280"
                  >
                    ${subjectProperty.price.toFixed(2)} • {(subjectProperty.marketShare * 100).toFixed(1)}% share
                  </text>
                </g>

                {/* Competitive Connections */}
                {analysisComplete && showInsights && (
                  <g stroke="rgba(139, 92, 246, 0.3)" strokeWidth="1" strokeDasharray="2,2">
                    {competitors.map((competitor, index) => (
                      <line
                        key={`connection-${index}`}
                        x1={subjectProperty.position.x}
                        y1={subjectProperty.position.y}
                        x2={competitor.position.x}
                        y2={competitor.position.y}
                        className="animate-draw-line"
                        style={{ animationDelay: `${index * 150 + 2000}ms` }}
                      />
                    ))}
                  </g>
                )}
              </svg>

              {/* Live Analysis Overlay */}
              {isAnalyzing && (
                <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-lg">
                  <div className="text-center">
                    <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                    <div className="text-lg font-semibold text-gray-900">{analysisPhase}</div>
                    <div className="text-sm text-gray-600">{progress}% Complete</div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Analysis Controls & Results */}
        <div className="space-y-4">
          {/* Market Position Card */}
          <Card className="p-4">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Market Position
            </h4>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Current Price:</span>
                <span className="font-medium">${subjectProperty.price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Market Share:</span>
                <span className="font-medium text-green-600">
                  {(subjectProperty.marketShare * 100).toFixed(1)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly Volume:</span>
                <span className="font-medium">{formatNumber(subjectProperty.volume)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Rank:</span>
                <span className="font-medium text-blue-600">#1 of 5</span>
              </div>
            </div>
          </Card>

          {/* Quick Insights */}
          {showInsights && (
            <Card className="p-4 animate-slide-up">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                Quick Insights
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <div className="font-medium">Market Leader</div>
                    <div className="text-gray-600">Highest volume and market share</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <div className="font-medium">Price Pressure</div>
                    <div className="text-gray-600">Arco aggressive pricing nearby</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <div className="font-medium">Growth Opportunity</div>
                    <div className="text-gray-600">Service enhancement potential</div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Selected Competitor Details */}
          {selectedCompetitor && (
            <Card className="p-4 animate-slide-up">
              {(() => {
                const competitor = competitors.find(c => c.id === selectedCompetitor);
                if (!competitor) return null;

                return (
                  <>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: competitor.color }}
                      />
                      {competitor.name}
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-gray-600">Distance</div>
                          <div className="font-medium">{competitor.distance} mi</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Price</div>
                          <div className="font-medium">${competitor.price.toFixed(2)}</div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div>
                          <div className="text-gray-600 text-xs mb-1">Strengths</div>
                          {competitor.strengths.map((strength, idx) => (
                            <div key={idx} className="text-xs text-green-700 bg-green-50 rounded px-2 py-1 mb-1">
                              {strength}
                            </div>
                          ))}
                        </div>
                        <div>
                          <div className="text-gray-600 text-xs mb-1">Weaknesses</div>
                          {competitor.weaknesses.map((weakness, idx) => (
                            <div key={idx} className="text-xs text-red-700 bg-red-50 rounded px-2 py-1 mb-1">
                              {weakness}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </Card>
          )}
        </div>
      </div>

      {/* Game Theory Results */}
      {analysisComplete && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {/* Nash Equilibrium */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Game Theory Analysis
            </h3>

            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-blue-900 mb-3">Nash Equilibrium</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-blue-700">Optimal Price:</div>
                  <div className="font-bold text-lg">${gameTheoryResults.optimalPrice.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-blue-700">Expected Volume:</div>
                  <div className="font-bold text-lg">{formatNumber(gameTheoryResults.expectedVolume)}</div>
                </div>
                <div>
                  <div className="text-blue-700">Projected Revenue:</div>
                  <div className="font-bold text-lg">{formatCurrency(gameTheoryResults.expectedRevenue)}</div>
                </div>
                <div>
                  <div className="text-blue-700">Market Share:</div>
                  <div className="font-bold text-lg">{(gameTheoryResults.marketShare * 100).toFixed(1)}%</div>
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              <p>
                Current strategy: <strong>{gameTheoryResults.currentStrategy}</strong>
              </p>
              <p className="mt-1">
                Optimal pricing considers competitor responses and market elasticity.
              </p>
            </div>
          </Card>

          {/* Strategic Scenarios */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Navigation className="w-5 h-5 text-green-600" />
              Strategic Scenarios
            </h3>

            <div className="space-y-4">
              {gameTheoryResults.scenarios.map((scenario, index) => (
                <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium">{scenario.name}</h4>
                    <span className={`text-sm px-2 py-1 rounded ${
                      scenario.roi >= 0.25 ? 'bg-green-100 text-green-700' :
                      scenario.roi >= 0.15 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {(scenario.roi * 100).toFixed(0)}% ROI
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                    <div>
                      <span className="text-gray-600">Volume:</span>
                      <span className="font-medium text-green-600 ml-1">
                        +{formatNumber(scenario.volumeChange)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Revenue:</span>
                      <span className="font-medium text-green-600 ml-1">
                        +{formatCurrency(scenario.revenueChange)}
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-600 space-y-1">
                    <div>Market Share: +{(scenario.marketShareGain * 100).toFixed(1)}%</div>
                    <div>Competitor Response: {scenario.competitorResponse}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Footer */}
      <Card className="p-6 bg-gray-50">
        <div className="text-center">
          <h3 className="font-semibold text-gray-900 mb-2">Advanced Competitive Intelligence</h3>
          <p className="text-sm text-gray-600 mb-4">
            Real-time competitor tracking • Game theory optimization • Strategic scenario planning
          </p>
          <div className="flex justify-center gap-6 text-xs text-gray-500">
            <span>Last updated: {new Date().toLocaleTimeString()}</span>
            <span>•</span>
            <span>Data refresh: Every 15 minutes</span>
            <span>•</span>
            <span>Analysis confidence: 94%</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Additional CSS for animations (add to your global CSS or styled components)
const additionalStyles = `
@keyframes bounce-in {
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.1); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slide-up {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes grow-width {
  from { width: 0; }
}

@keyframes draw-line {
  from { stroke-dasharray: 1000; stroke-dashoffset: 1000; }
  to { stroke-dasharray: 1000; stroke-dashoffset: 0; }
}

.animate-bounce-in { animation: bounce-in 0.6s ease-out forwards; }
.animate-fade-in { animation: fade-in 0.8s ease-out forwards; }
.animate-slide-up { animation: slide-up 0.5s ease-out forwards; }
.animate-grow-width { animation: grow-width 0.8s ease-out forwards; }
.animate-draw-line { animation: draw-line 1s ease-out forwards; }
`;